import React, { ReactNode, useEffect, useRef } from 'react';
import { fetchCsrfCookie } from '@api-client/csrf';
import StoreContext from './StoreContext';
import useAuthStore from '../stores/auth.store';
import useInterfaceStore from '../stores/interface.store';
import useToastStore from '../stores/toast.store';
import useBdxStore from '../stores/bdxImport.store';

type StoreProviderProps = {
  children: ReactNode;
};

const StoreProvider: React.FC<StoreProviderProps> = ({ children }) => {
  const authStore = useAuthStore();
  const interfaceStore = useInterfaceStore();
  const toast = useToastStore();
  const bdxImportStore = useBdxStore();
  const initialised = useRef<boolean>(false);

  const store = {
    authStore,
    interfaceStore,
    bdxImportStore,
    toast
  };

  const initialize = async () => {
    if (!authStore.token) {
      return;
    }

    try {
      await fetchCsrfCookie();
    } catch (e) {
      console.error('[StoreProvider] Failed to fetch CSRF cookie:', e);
    }

    // CSRF bootstrap prepares cookie/session state for stateful requests (login,
    // logout, passport web session). refreshUser uses Bearer-token Axios calls
    // to /me and does not depend on the CSRF cookie, so we continue best-effort
    // so an existing session can still hydrate even if sanctum fetch fails.
    await authStore.refreshUser();
  };

  // Fetch user on start
  useEffect(() => {
    if (!initialised.current) {
      initialised.current = true;
      initialize().then();
    }
  }, []);

  return (
    <StoreContext.Provider value={store}>{children}</StoreContext.Provider>
  );
};

export default StoreProvider;
